import { z } from "zod";
import {
  AUDIT_DOMAINS,
  AUDIT_OUTCOMES,
  AUDIT_STREAMS,
} from "@yazamco/shared-types";

/**
 * Audit Log query validation.
 *
 * `category` keeps its historical meaning — the PRODUCT AREA (customer, server,
 * …) — so existing clients and saved filters are unaffected. Its value list is
 * widened to the full domain set, which is backward compatible: the old ten
 * values are all still present, and the modules the old read-time ladder had no
 * case for (billing, identity, m365, gdap, service, customer_audit, …) are now
 * addressable instead of collapsing into `other`.
 *
 * `stream` is the new facet: which of the five logs to read. Omitting it is what
 * makes the default feed high-signal — see auditQuerySchema below.
 */
export const AUDIT_CATEGORIES = AUDIT_DOMAINS;

export const auditQuerySchema = z.object({
  search: z.string().trim().max(200).optional(),
  category: z.enum(AUDIT_CATEGORIES).optional(),
  /**
   * Which log. Deliberately NOT defaulted here: the service distinguishes
   * "unspecified" (→ the user-facing streams, audit + security) from an explicit
   * single stream, and a default in the schema would erase that distinction.
   */
  stream: z.enum(AUDIT_STREAMS).optional(),
  outcome: z.enum(AUDIT_OUTCOMES).optional(),
  entityType: z.string().trim().max(64).optional(),
  actor: z.string().trim().max(320).optional(),
  from: z.string().datetime().optional().or(z.string().trim().length(0).optional()),
  to: z.string().datetime().optional().or(z.string().trim().length(0).optional()),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export type AuditQueryInput = z.infer<typeof auditQuerySchema>;

export const auditExportQuerySchema = auditQuerySchema.extend({
  format: z.enum(["csv", "json"]).default("json"),
});

export type AuditExportQueryInput = z.infer<typeof auditExportQuerySchema>;

// ── Retention ───────────────────────────────────────────────────────────────

/**
 * A retention period, in days, or null for "keep indefinitely".
 *
 * The floor is 30 days, not 1. A single mistyped digit otherwise means "delete
 * almost everything", and that mistake is not recoverable — unlike setting the
 * number too high, which is merely a policy problem someone can fix later.
 */
const retentionDays = z
  .number()
  .int()
  .min(30, "A retention period shorter than 30 days is almost certainly a typo")
  .max(3650)
  .nullable();

export const auditRetentionSchema = z.object({
  /**
   * Independent of the periods, so an administrator can stage the intended
   * numbers, read the preview and leave them under review without any risk that
   * saving the form starts destroying history.
   */
  enabled: z.boolean(),
  periods: z.object({
    audit: retentionDays,
    security: retentionDays,
    system: retentionDays,
    integration: retentionDays,
    error: retentionDays,
  }),
  /** Why these periods — the legal basis or policy reference. */
  note: z.string().trim().max(500).nullish(),
});

export type AuditRetentionInput = z.infer<typeof auditRetentionSchema>;
